// client/src/pages/pmPortal/TenantLeasePage.js
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Loading, Notice, Pill, Field, money, fmtDate, titleCase, statusTone }
    from '../../components/secure/pm/PMKit';

const TenantLeasePage = () => {
    const api = usePmPortalApiClient();
    const [leases, setLeases] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/pm-portal/tenant/leases').then(setLeases).catch(e => setError(e.message));
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load your lease" message={error} />;
    if (!leases) return <Loading />;

    return (
        <>
            <PageHeader
                title="Your lease"
                subtitle="A lease means what it meant when it was signed. A later change to an ordinance alters what is owed going forward, not the terms you agreed to."
            />

            {leases.length === 0 && (
                <Notice tone="warn" title="No lease on file" message="Contact the office if you believe this is wrong." />
            )}

            {leases.map(l => (
                <Panel
                    key={l.lease_id}
                    title={`${l.property_name}${l.unit_number ? ` · Unit ${l.unit_number}` : ''}`}
                    subtitle={l.lease_no ? `Lease ${l.lease_no}` : null}
                    actions={<Pill tone={statusTone(l.status)}>{titleCase(l.status)}</Pill>}
                >
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        <Field label="Your role" value={titleCase(l.tenant_role)} />
                        <Field label="Lease type" value={titleCase(l.lease_type)} />
                        <Field label="Term" value={`${fmtDate(l.start_date)} – ${fmtDate(l.end_date)}`} />
                        <Field label="Moved in" value={fmtDate(l.move_in_on)} />
                        <Field label="Rent" value={money(l.rent_amount)} />
                        <Field
                            label="Due each month on"
                            value={l.rent_due_day ? `Day ${l.rent_due_day}` : null}
                            hint={l.grace_days ? `${l.grace_days} day grace period before a late fee may apply.` : 'No grace period.'}
                        />
                        <Field
                            label="Security deposit"
                            value={money(l.security_deposit)}
                            hint={l.deposit_received_on ? `Received ${fmtDate(l.deposit_received_on)} and held in a special account under 68 IAC 1450.750.` : null}
                        />
                        <Field label="Moved out" value={fmtDate(l.move_out_on)} />
                        <Field
                            label="Address"
                            value={[l.street_address, l.city, l.state, l.zip_code].filter(Boolean).join(', ')}
                        />
                        <Field
                            label="Unit"
                            value={[
                                l.bedrooms ? `${l.bedrooms} bed` : null,
                                l.bathrooms ? `${l.bathrooms} bath` : null,
                                l.square_feet ? `${l.square_feet} sq ft` : null,
                            ].filter(Boolean).join(' · ')}
                        />
                    </dl>
                </Panel>
            ))}
        </>
    );
};

export default TenantLeasePage;
